import React from "react";
import axios from "axios";
import { notifications } from '@mantine/notifications';
import { Group, Text, useMantineTheme, Paper, Loader, Textarea } from '@mantine/core';
import { IconX, IconChevronRight } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_THREE, utils } from "../../utils/globalVariables";
import { getPropInSubLevelRole, safeValue, sqlDateToIsoDate, sqlDateToHumanDate, isoDateToSqlDateTime } from "../../utils/globalFunctions";

import ModalResponse from "../../components/general/ModalResponse";
import ModalTakeAction from "../../components/general/ModalTakeAction";
import InputWrapperCustom from "../../components/general/InputWrapperCustom";
import InputComponent from "../../components/general/InputComponent";
import LabelWithAsterisk from "../../components/general/LabelWithAsterisk";
import ActionIconComponent from "../../components/general/ActionIconComponent";

import PaperWrapper from "../../components/utilities/PaperWrapper";

import DeliveryManSelector from "../../components/utilities/DeliveryManSelector";
import DateTimePickerModal from "../../components/general/DateTimePickerModal";

const ComponentDeliveryUtils = ({
    dataDataKey,
    stateKey,
    utilsRef,
    fnRefreshIndex,
    fnDeliveryMan,
    fnWaktuPenjemputan,
    fnKeterangan,
}) => {

    const mountedRef = React.useRef(true);

    const theme = useMantineTheme();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorSubmitDeliveryMan: 'deliveryJemputNotaBasicData-7',
        errorSubmitWaktuJemput: 'deliveryJemputNotaBasicData-8',
        errorSubmitKeterangan: 'deliveryJemputNotaBasicData-9',
    })

    const canEditBarang = React.useRef([
        utils.penjemputanSteps.nota.value,
        utils.penjemputanSteps.segera_menjemput.value,
    ])

    const [data, setData] = React.useState({
        delivery: {
            modal: false,
            dataInitLoading: false,
            data: [],
            dataSelectedError: '',

            trxId: '',
            stateKey: '',
            submitLoading: false,

            deleteModal: false,
        },

        waktuJemput: {
            modal: false,
            trxId: '',
            stateKey: '',

            value: new Date(),
            valueError: '',
            submitLoading: false,

            deleteModal: false,
            deleteSubmitLoading: false
        },

        keterangan: {
            modal: false,
            trxId: '',
            stateKey: '',

            value: '',
            submitLoading: false
        },

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const submitDeliveryMan = (object) => {
        setData(d => ({ ...d, delivery: { ...d.delivery, submitLoading: true } }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('trx_id', data.delivery.trxId);
        formDT.append('current_step', utilsRef.currentStep);
        formDT.append('is_cancel', utilsRef.isCancel);
        formDT.append('user_id', object ? object.id : '');

        axios({
            method: 'post',
            url: BE_API_THREE + '/update_delivery_man__penjemputan',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, delivery: { ...d.delivery, submitLoading: false, modal: false } }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    delivery: {
                        ...d.delivery,
                        deleteModal: false
                    }
                }));

                fnDeliveryMan(data.delivery.stateKey, object);
                fnRefreshIndex();
            }

            if (response.data.flag_status === 'error') {
                notifications.show({
                    id: notificationId.current.errorSubmitDeliveryMan,
                    title: utils.notification.warning.caption,
                    message: response.data.pesan,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: true,
                })
            }

            if (response.data.flag_status === 'no-data') {
                setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        type: utils.modalResponse.warning,
                        hideOnClickOutSide: true,
                        content: 'Data tidak ditemukan.',
                        show: true
                    }
                }))
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, delivery: { ...d.delivery, submitLoading: false, modal: false } }));

            notifications.show({
                id: notificationId.current.errorSubmitDeliveryMan,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }

    const submitWaktuJemput = (stateDate) => {
        setData(d => ({ ...d, waktuJemput: { ...d.waktuJemput, submitLoading: true } }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('trx_id', data.waktuJemput.trxId);
        formDT.append('current_step', utilsRef.currentStep);
        formDT.append('is_cancel', utilsRef.isCancel);
        formDT.append('value', isoDateToSqlDateTime(stateDate, ''));

        axios({
            method: 'post',
            url: BE_API_THREE + '/update_waktu_jemput__penjemputan',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d, waktuJemput: {
                    ...d.waktuJemput,
                    submitLoading: false,
                    modal: false,
                    deleteSubmitLoading: false,
                    deleteModal: false,
                }
            }));

            if (response.data.flag_status === 'isset') {
                fnWaktuPenjemputan(data.waktuJemput.stateKey, isoDateToSqlDateTime(stateDate, null));
                fnRefreshIndex();
            }

            if (response.data.flag_status === 'error') {
                notifications.show({
                    id: notificationId.current.errorSubmitWaktuJemput,
                    title: utils.notification.warning.caption,
                    message: response.data.pesan,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: true,
                })
            }

            if (response.data.flag_status === 'no-data') {
                setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        type: utils.modalResponse.warning,
                        hideOnClickOutSide: true,
                        content: 'Data tidak ditemukan.',
                        show: true
                    }
                }))
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d, waktuJemput: {
                    ...d.waktuJemput,
                    submitLoading: false,
                    modal: false,
                    deleteSubmitLoading: false,
                    deleteModal: false,
                }
            }));

            notifications.show({
                id: notificationId.current.errorSubmitWaktuJemput,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }

    const submitKeterangan = () => {
        setData(d => ({ ...d, keterangan: { ...d.keterangan, submitLoading: true } }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('trx_id', data.keterangan.trxId);
        formDT.append('current_step', utilsRef.currentStep);
        formDT.append('is_cancel', utilsRef.isCancel);
        formDT.append('value', data.keterangan.value);

        axios({
            method: 'post',
            url: BE_API_THREE + '/update_keterangan__penjemputan',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, keterangan: { ...d.keterangan, submitLoading: false, modal: false } }));

            if (response.data.flag_status === 'isset') {
                fnKeterangan(data.keterangan.stateKey, data.keterangan.value);
            }

            if (response.data.flag_status === 'error') {
                notifications.show({
                    id: notificationId.current.errorSubmitKeterangan,
                    title: utils.notification.warning.caption,
                    message: response.data.pesan,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: true,
                })
            }

            if (response.data.flag_status === 'no-data') {
                setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        type: utils.modalResponse.warning,
                        hideOnClickOutSide: true,
                        content: 'Data tidak ditemukan.',
                        show: true
                    }
                }))
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, keterangan: { ...d.keterangan, submitLoading: false, modal: false } }));

            notifications.show({
                id: notificationId.current.errorSubmitKeterangan,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }

    return (
        <React.Fragment>
            <PaperWrapper>
                <InputWrapperCustom
                    stateLabel={
                        <Group spacing={6}>
                            <LabelWithAsterisk stateLabel='Delivery Man' />
                            {data.delivery.submitLoading && <Loader size="xs" />}
                        </Group>
                    }
                    stateWithAsterisk={false}
                    stateError=''
                    stateDescription=''
                    stateMB="1.5rem"
                >
                    <Paper
                        sx={(tema) => ({
                            backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[7],
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: tema.colorScheme === 'light' ? tema.colors.gray[4] : tema.colors.dark[4],
                            cursor: 'pointer',
                        })}

                        onClick={!canEditBarang.current.includes(utilsRef.currentStep) ?
                            () => { }
                            :
                            () => {
                                setData(d => ({
                                    ...d,
                                    delivery: {
                                        ...d.delivery,
                                        trxId: dataDataKey.delivery_jemput_id,
                                        stateKey: stateKey,
                                        modal: true
                                    }
                                }));
                            }}
                    >
                        {!dataDataKey.delivery_man_id &&
                            <Group
                                position="apart"
                                noWrap
                                p="xs"
                            >
                                <Text size='sm'>Pilih Delivery Man</Text>
                                <IconChevronRight size="1rem" />
                            </Group>
                        }

                        {dataDataKey.delivery_man_id &&
                            <Group
                                position="apart"
                                noWrap
                                p="xs"
                            >
                                <Group noWrap>
                                    <Text size='sm'>
                                        <Text>
                                            {dataDataKey.delivery_man_name}
                                        </Text>

                                        <Text size='xs'>
                                            {getPropInSubLevelRole(dataDataKey.delivery_man_sub_level, 'caption', '-')}
                                        </Text>
                                    </Text>
                                </Group>

                                {canEditBarang.current.includes(utilsRef.currentStep) &&
                                    <ActionIconComponent
                                        stateVariant='light'
                                        stateRadius='xl'
                                        stateTitle='Hapus'
                                        stateSize='lg'
                                        fnOnClick={(e) => {
                                            e.stopPropagation();
                                            setData(d => ({
                                                ...d,
                                                delivery: {
                                                    ...d.delivery,
                                                    trxId: dataDataKey.delivery_jemput_id,
                                                    stateKey: stateKey,
                                                    deleteModal: true
                                                }
                                            }))
                                        }}

                                        StateIcon={IconX}
                                        stateIconSize='1.125rem'
                                        stateIconStrokeWidth={1.25}
                                        stateIconStyle={{
                                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                                        }}
                                    />
                                }
                            </Group>
                        }
                    </Paper>
                </InputWrapperCustom>

                <InputComponent
                    stateId='input-waktu-jemput-1'
                    stateWithAsterisk={true}
                    stateLabelFullWidth={false}
                    stateLabel='Waktu Jemput'
                    stateDescription=''
                    statePlaceHolder='Waktu Jemput'
                    stateReadOnly={true}
                    stateValue={sqlDateToHumanDate({
                        stateDate: dataDataKey.waktu_jemput,
                        stateWithTime: true,
                        stateLang: 'id',
                        stateIsShort: false,
                        stateDay: true,
                        stateDefaultValue: '',
                        stateDateType: 'day',
                        stateWithSecond: false
                    })}
                    stateError={data.waktuJemput.valueError}
                    stateRightSection={
                        dataDataKey.waktu_jemput && canEditBarang.current.includes(utilsRef.currentStep) &&
                        <IconX
                            size='0.8rem'
                            style={{ cursor: 'pointer' }}

                            onClick={() => {
                                setData(d => ({
                                    ...d,
                                    waktuJemput: {
                                        ...d.waktuJemput,
                                        trxId: dataDataKey.delivery_jemput_id,
                                        stateKey: stateKey,
                                        deleteModal: true,
                                    }
                                }))
                            }}
                        />
                    }
                    stateStylesInput={{
                        backgroundColor: theme.colorScheme === 'light' ? '#fff' : theme.colors.dark[7],
                    }}
                    stateMB="1.5rem"
                    fnOnClick={!canEditBarang.current.includes(utilsRef.currentStep) ?
                        () => { }
                        :
                        () => {
                            setData(d => ({
                                ...d,
                                waktuJemput: {
                                    ...d.waktuJemput,
                                    trxId: dataDataKey.delivery_jemput_id,
                                    value: sqlDateToIsoDate(dataDataKey.waktu_jemput, new Date()),
                                    stateKey: stateKey,
                                    modal: true,
                                }
                            }))
                        }}
                />

                <Textarea
                    value={safeValue(dataDataKey.keterangan, '')}

                    onClick={!canEditBarang.current.includes(utilsRef.currentStep) ?
                        () => { }
                        :
                        () => setData(d => ({
                            ...d,
                            keterangan: {
                                ...d.keterangan,
                                trxId: dataDataKey.delivery_jemput_id,
                                stateKey: stateKey,
                                value: dataDataKey.keterangan,
                                modal: true
                            }
                        }))}

                    readOnly={true}

                    styles={(tema) => ({
                        input: {
                            backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[7]
                        }
                    })}

                    autosize
                    minRows={2}
                    maxRows={5}
                    label='Keterangan'
                    placeholder='Keterangan / Catatan'
                />
            </PaperWrapper>

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />

            <DeliveryManSelector
                stateShow={data.delivery.modal}
                fnShow={() => setData(d => ({ ...d, delivery: { ...d.delivery, modal: false } }))}
                fnOnSelect={(object) => {
                    submitDeliveryMan(object);
                    setData(d => ({ ...d, delivery: { ...d.delivery, modal: false } }));
                }}
                stateData={{
                    isActive: '1',
                    gender: utils.flagAll
                }}
            />

            <ModalTakeAction
                stateShow={data.delivery.deleteModal}
                fnHide={() => setData(d => ({ ...d, delivery: { ...d.delivery, deleteModal: false } }))}

                stateHideOnClickOutSide={true}
                stateTitle={String('Hapus Delivery Man').toUpperCase()}
                stateOverlayZIndex={201}

                stateButtonClosePx='md'

                stateButtonActionCaption='Submit'
                stateButtonActionPx='xs'
                stateButtonActionColor="blue"
                stateButtonActionLoading={data.delivery.submitLoading}
                fnButtonAction={() => submitDeliveryMan(null)}
            >
                <Text size='sm' color="dimmed">Hapus Delivery Man ?</Text>
            </ModalTakeAction>

            <DateTimePickerModal
                stateShow={data.waktuJemput.modal}
                fnHide={() => setData(d => ({ ...d, waktuJemput: { ...d.waktuJemput, modal: false } }))}

                stateData={{
                    valueDate: data.waktuJemput.value
                }}

                stateHideOnClickOutSide={true}
                stateOverlayZIndex={201}

                stateButtonClosePx='md'

                stateButtonActionCaption='Submit'
                stateButtonActionPx='xs'
                stateButtonActionColor="blue"
                stateButtonActionLoading={data.waktuJemput.submitLoading}
                fnButtonAction={(valDate) => submitWaktuJemput(valDate)}
            />

            <ModalTakeAction
                stateShow={data.waktuJemput.deleteModal}
                fnHide={() => setData(d => ({ ...d, waktuJemput: { ...d.waktuJemput, deleteModal: false } }))}

                stateHideOnClickOutSide={true}
                stateTitle={String('Confirmation').toUpperCase()}
                stateOverlayZIndex={201}

                stateButtonClosePx='md'

                stateButtonActionCaption='Submit'
                stateButtonActionPx='xs'
                stateButtonActionColor="blue"
                stateButtonActionLoading={data.waktuJemput.deleteSubmitLoading}
                fnButtonAction={() => submitWaktuJemput(null)}
            >
                <Text size='sm' color="dimmed">Hapus Waktu Penjemputan ?</Text>
            </ModalTakeAction>

            <ModalTakeAction
                stateShow={data.keterangan.modal}
                fnHide={() => setData(d => ({ ...d, keterangan: { ...d.keterangan, modal: false } }))}

                stateHideOnClickOutSide={true}
                stateTitle={String('Keterangan').toUpperCase()}
                stateOverlayZIndex={201}

                stateButtonClosePx='md'

                stateButtonActionCaption='Submit'
                stateButtonActionPx='xs'
                stateButtonActionColor="blue"
                stateButtonActionLoading={data.keterangan.submitLoading}
                fnButtonAction={submitKeterangan}
            >
                <Textarea
                    value={safeValue(data.keterangan.value, '')}
                    minRows={3}
                    maxRows={4}
                    onChange={(e) => setData(d => ({ ...d, keterangan: { ...d.keterangan, value: e.target.value } }))}

                    label=''
                    placeholder='Keterangan / Catatan'
                    error=''
                    description=''
                    withAsterisk={false}
                />
            </ModalTakeAction>
        </React.Fragment>
    )
}

export default ComponentDeliveryUtils;